import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Calendar, Save, X, Megaphone } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'depistage', label: 'D\u00e9pistage' },
  { value: 'trod', label: 'TROD' },
  { value: 'entretien', label: 'Entretien' },
  { value: 'prevention', label: 'Pr\u00e9vention' },
];

const COLORS = [
  '#DC2626', '#7C3AED', '#EC4899', '#2563EB', '#F472B6',
  '#F59E0B', '#06B6D4', '#059669', '#B91C1C', '#0891B2',
  '#78716C', '#4F46E5', '#EAB308', '#92400E',
];

export default function AdminCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    fetch(`${API_URL}/admin/campaigns`, { headers })
      .then(r => r.json())
      .then(data => { setCampaigns(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSeed = async () => {
    setSeeding(true);
    await fetch(`${API_URL}/admin/campaigns/seed`, { method: 'POST', headers });
    load();
    setSeeding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    await fetch(`${API_URL}/admin/campaigns/${id}`, { method: 'DELETE', headers });
    load();
  };

  const handleSave = async (data) => {
    if (data.id) {
      await fetch(`${API_URL}/admin/campaigns/${data.id}`, { method: 'PUT', headers, body: JSON.stringify(data) });
    } else {
      await fetch(`${API_URL}/admin/campaigns`, { method: 'POST', headers, body: JSON.stringify(data) });
    }
    setEditing(null);
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-[#1B2A4A]">Campagnes Santé</h1>
          <p className="text-sm text-gray-500">Gérez le référentiel des campagnes de santé publique</p>
        </div>
        <div className="flex gap-2">
          {campaigns.length === 0 && (
            <button onClick={handleSeed} disabled={seeding} className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#9A7009] disabled:opacity-50" data-testid="seed-campaigns-btn">
              <Calendar size={16} /> {seeding ? 'Chargement...' : 'Charger le r\u00e9f\u00e9rentiel'}
            </button>
          )}
          <button onClick={() => setEditing({ name: '', short_name: '', category: 'vaccination', period_start_month: 1, period_start_day: 1, period_end_month: 12, period_end_day: 31, target_audience: {}, remuneration_per_act: 0, actions_recommended: [], color: '#2563EB', is_active: true })} className="flex items-center gap-2 bg-[#1B2A4A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#152039]" data-testid="add-campaign-btn">
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </div>

      {/* Campaign list */}
      <div className="space-y-2">
        {campaigns.map(c => (
          <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4" data-testid={`admin-campaign-${c.id}`}>
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-[#1B2A4A] text-sm">{c.name}</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-600">{c.category}</span>
                {!c.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600">Inactive</span>}
              </div>
              <p className="text-xs text-gray-400">{c.period_label || `Mois ${c.period_start_month} - ${c.period_end_month}`} | {c.remuneration_per_act > 0 ? `${c.remuneration_per_act}\u20ac/acte` : 'Non r\u00e9mun\u00e9r\u00e9'}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing({...c})} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A]"><Pencil size={16} /></button>
              <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {campaigns.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <Megaphone size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucune campagne</p>
            <p className="text-gray-400 text-sm">Cliquez sur "Charger le référentiel" pour importer les campagnes françaises</p>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#1B2A4A]">{editing.id ? 'Modifier' : 'Nouvelle campagne'}</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded-lg hover:bg-gray-100"><X size={20} /></button>
            </div>
            <CampaignForm campaign={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignForm({ campaign, onSave, onCancel }) {
  const [form, setForm] = useState(campaign);
  const [actionsText, setActionsText] = useState((campaign.actions_recommended || []).join('\n'));

  const handleSubmit = () => {
    onSave({
      ...form,
      actions_recommended: actionsText.split('\n').filter(Boolean),
      remuneration_per_act: parseFloat(form.remuneration_per_act) || 0,
      period_start_month: parseInt(form.period_start_month) || 1,
      period_start_day: parseInt(form.period_start_day) || 1,
      period_end_month: parseInt(form.period_end_month) || 12,
      period_end_day: parseInt(form.period_end_day) || 28,
      captation_rate_default: parseFloat(form.captation_rate_default) || 0.15,
    });
  };

  const Field = ({ label, name, type = 'text', ...props }) => (
    <div>
      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input type={type} value={form[name] || ''} onChange={e => setForm({ ...form, [name]: e.target.value })}
        className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" {...props} />
    </div>
  );

  return (
    <div className="space-y-3">
      <Field label="Nom" name="name" />
      <Field label="Nom court" name="short_name" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Catégorie</label>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Couleur</label>
          <div className="flex gap-1 flex-wrap">
            {COLORS.map(c => (
              <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-6 h-6 rounded-md border-2 ${form.color === c ? 'border-[#1B2A4A] scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Field label="Mois début" name="period_start_month" type="number" min="1" max="12" />
        <Field label="Jour début" name="period_start_day" type="number" min="1" max="31" />
        <Field label="Mois fin" name="period_end_month" type="number" min="1" max="12" />
        <Field label="Jour fin" name="period_end_day" type="number" min="1" max="31" />
      </div>
      <Field label="Période (texte)" name="period_label" placeholder="ex: Octobre - Janvier" />
      <Field label="Résumé de la cible" name="target_summary" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rémunération par acte (€)" name="remuneration_per_act" type="number" step="0.01" />
        <Field label="Taux de captation" name="captation_rate_default" type="number" step="0.01" placeholder="0.15" />
      </div>
      <Field label="Source de rémunération" name="remuneration_source" />
      <div>
        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Description</label>
        <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
      </div>
      <div>
        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Actions recommandées (une par ligne)</label>
        <textarea value={actionsText} onChange={e => setActionsText(e.target.value)} rows={4}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({ ...form, is_active: e.target.checked })} id="active" />
        <label htmlFor="active" className="text-sm text-gray-600">Campagne active</label>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={handleSubmit} className="flex-1 flex items-center justify-center gap-2 bg-[#1B2A4A] text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039]">
          <Save size={16} /> Enregistrer
        </button>
        <button onClick={onCancel} className="px-6 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Annuler</button>
      </div>
    </div>
  );
}
